import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';

/**
 * AccuracyLineChart - Line chart showing accuracy over time
 * 
 * @param {Object} props
 * @param {Array<number>} props.accuracyByEvent - Array of accuracy percentages per event (0-1)
 * @param {number} props.accuracyPctLastEvent - Most recent accuracy value (for highlighting)
 * @param {string} props.trainingDataCutoffDate - Date string for cutoff (events before this are training)
 * @param {Array<string>} props.eventDates - Array of event dates corresponding to accuracyByEvent
 */
export default function AccuracyLineChart({ accuracyByEvent = [], accuracyPctLastEvent, trainingDataCutoffDate, eventDates }) {
  if (!accuracyByEvent || accuracyByEvent.length === 0) {
    return (
      <div className="chart-empty">
        No accuracy data available
      </div>
    );
  }

  // Helper function to parse date string without timezone issues
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Transform data for chart with dates and training/actual classification
  // Create separate dataKeys for training and actual so we can render two distinct lines
  const data = accuracyByEvent.map((accuracy, index) => {
    const eventDate = eventDates?.[index] || null;
    const isTraining = eventDate && trainingDataCutoffDate 
      ? parseDate(eventDate) < parseDate(trainingDataCutoffDate)  // Strictly less than (BEFORE cutoff date)
      : false;
    
    const accuracyPercent = parseFloat((accuracy * 100).toFixed(2));
    
    return {
      event: index + 1,
      accuracy: accuracyPercent, // Keep for backward compatibility
      rawAccuracy: accuracy,
      date: eventDate,
      isTraining: isTraining,
      // Separate dataKeys for training vs actual lines
      trainingAccuracy: isTraining ? accuracyPercent : null,
      actualAccuracy: !isTraining ? accuracyPercent : null
    };
  });

  // Determine if we should use dates on X-axis (if we have dates and cutoff date)
  const useDates = eventDates && eventDates.length > 0 && trainingDataCutoffDate;
  const hasTrainingData = trainingDataCutoffDate && data.some(d => d.isTraining);
  const hasActualData = trainingDataCutoffDate && data.some(d => !d.isTraining);
  
  // Find the cutoff event index for the vertical separator
  const cutoffEventIndex = useDates ? data.findIndex(d => d.date && parseDate(d.date) >= parseDate(trainingDataCutoffDate)) : -1;

  // Custom label component for training cutoff
  const TrainingCutoffLabel = ({ viewBox }) => {
    if (!viewBox) return null;
    // Position label above the chart area
    const labelY = Math.max(5, viewBox.y - 5);
    return (
      <g>
        <text
          x={viewBox.x}
          y={labelY}
          fill="#ECE9F3"
          fontSize={14}
          fontWeight={700}
          textAnchor="middle"
          style={{ 
            pointerEvents: 'none'
          }}
        >
          Training cutoff
        </text>
      </g>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = parseDate(dateString);
        if (!date) return null;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      };
      
      return (
        <div style={{
          backgroundColor: '#15121D',
          padding: '8px 12px',
          borderRadius: '8px',
          border: '1px solid #3A3349'
        }}>
          <p style={{ margin: 0, color: '#ECE9F3', fontSize: '12px' }}>
            {data.date ? formatDate(data.date) : `Event ${data.event}`}
          </p>
          <p style={{ margin: '4px 0 0 0', color: '#3DD68C', fontSize: '11px' }}>
            Accuracy: {(data.accuracy || data.trainingAccuracy || data.actualAccuracy || 0).toFixed(2)}%
          </p>
          {data.isTraining !== undefined && (
            <p style={{ margin: '4px 0 0 0', color: data.isTraining ? '#6E6780' : '#3DD68C', fontSize: '10px' }}>
              {data.isTraining ? 'Training data' : 'Actual performance'}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="chart-block">
      <h3>
        Accuracy over time
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 40, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2A2536" />
          <XAxis 
            dataKey={useDates ? "date" : "event"}
            stroke="#6E6780"
            tick={{ fill: '#9A93AB', fontSize: 12 }}
            tickFormatter={useDates ? (value) => {
              if (!value) return '';
              const date = parseDate(value);
              if (!date) return '';
              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            } : undefined}
            label={{ 
              value: useDates ? 'Date' : 'Event number', 
              position: 'insideBottom', 
              offset: -5,
              style: { fill: '#9A93AB', fontSize: 12 }
            }}
          />
          <YAxis 
            stroke="#6E6780"
            tick={{ fill: '#9A93AB', fontSize: 12 }}
            domain={[0, 100]}
            label={{ 
              value: 'Accuracy (%)', 
              angle: -90, 
              position: 'insideLeft',
              style: { fill: '#9A93AB', fontSize: 12 }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="line"
          />
          
          {/* Training data line (if we have training data and cutoff date) */}
          {hasTrainingData && (
            <Line 
              type="monotone" 
              dataKey="trainingAccuracy" 
              stroke="#6E6780"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 3, fill: '#6E6780' }}
              activeDot={{ r: 5, fill: '#6E6780' }}
              name="Training data"
              connectNulls={false}
            />
          )}
          
          {/* Actual data line */}
          {hasActualData ? (
            <Line 
              type="monotone" 
              dataKey="actualAccuracy" 
              stroke="#3DD68C" 
              strokeWidth={2}
              dot={{ r: 3, fill: '#3DD68C' }}
              activeDot={{ r: 5, fill: '#3DD68C' }}
              name="Actual performance"
              connectNulls={false}
            />
          ) : (
            // Fallback: single line if no training/actual separation
            <Line 
              type="monotone" 
              dataKey="accuracy" 
              stroke="#3DD68C" 
              strokeWidth={2}
              dot={{ r: 3, fill: '#3DD68C' }}
              activeDot={{ r: 5, fill: '#3DD68C' }}
            />
          )}
          
          {/* Vertical separator at cutoff date */}
          {cutoffEventIndex >= 0 && trainingDataCutoffDate && (
            <ReferenceLine 
              x={useDates ? data[cutoffEventIndex]?.date : data[cutoffEventIndex]?.event}
              stroke="#ECE9F3" 
              strokeWidth={3}
              strokeDasharray="5 5"
              strokeOpacity={1}
              label={<TrainingCutoffLabel />}
            />
          )}
          
          {accuracyPctLastEvent !== undefined && accuracyPctLastEvent !== null && (
            <ReferenceLine 
              y={accuracyPctLastEvent * 100} 
              stroke="#FF6B6B" 
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{ 
                value: 'Last event', 
                position: 'right',
                fill: '#FF6B6B',
                fontSize: 12
              }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

